// ClientProfileSummary (TASK-580) — the narrative client-profile summary the
// generate_client_profile skill writes (5 structured sections + confidence),
// rendered as a FILTERABLE canvas tile: the UI brain (or the on-tile bar) can
// narrow to specific sections ("just risk & objectives") via the TASK-495
// filter contract. Self-fetches by clientId via clientService; legacy rows
// without structured sections fall back to the prose blob.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientService, type ClientProfileSummaryData } from '../../../services/clientService';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetFilterBar } from '../WidgetFilterBar';

export const SECTION_KEYS = [
  'background',
  'risk_tolerance',
  'objectives',
  'financial_situation',
  'preferences',
] as const;

function is404(err: unknown): boolean {
  const e = err as { response?: { status?: number }; message?: string };
  return e?.response?.status === 404 || Boolean(e?.message?.includes('404'));
}

const SECTION_LABEL: Record<string, string> = {
  background: 'Background & classification',
  risk_tolerance: 'Risk tolerance & experience',
  objectives: 'Objectives & time horizon',
  financial_situation: 'Financial situation',
  preferences: 'Preferences & constraints',
};

export interface ClientProfileSummaryFilters {
  sections?: string[];
  minConfidence?: number;
}

export function ClientProfileSummary({
  clientId,
  filters,
}: {
  clientId: string;
  filters?: ClientProfileSummaryFilters & Record<string, unknown>;
}) {
  const [active, setActive] = useState<ClientProfileSummaryFilters & Record<string, unknown>>(
    filters ?? {},
  );
  const { data, isLoading, isError, error } = useQuery<ClientProfileSummaryData>({
    queryKey: ['client-profile-summary', clientId],
    queryFn: () => clientService.getLatestProfileSummary(clientId),
    enabled: !!clientId,
    retry: false, // 404 = no summary yet, not a transient failure
  });

  const sections = data?.sections ?? null;
  const wanted = active.sections?.length
    ? SECTION_KEYS.filter((k) => active.sections!.includes(k))
    : [...SECTION_KEYS];
  // Sections actually renderable after the filter; an all-empty {} row or an
  // over-narrow filter must not leave a silently blank tile.
  const visible = sections ? wanted.filter((k) => (sections[k] ?? '').trim()) : [];
  const belowConfidence =
    data != null &&
    typeof active.minConfidence === 'number' &&
    data.confidence_score < active.minConfidence;

  return (
    <WidgetContainer
      title="Client profile"
      source="Meeting Prep · AI summary"
      // TASK-825: when this summary was generated. The tile rendered the date inline already
      // and still stamped its header with the mount clock.
      dataAsOf={data?.generated_at ?? null}
      toolbar={
        <WidgetFilterBar
          component="ClientProfileSummary"
          values={active}
          onChange={(partial) => setActive((prev) => ({ ...prev, ...partial }))}
        />
      }
    >
      {isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {isError && is404(error) && (
        <div className="text-[13px] text-muted-foreground">
          No profile summary yet for this client — generate one from a meeting's prepared
          documents.
        </div>
      )}
      {isError && !is404(error) && (
        <div className="text-[13px] text-destructive">
          Could not load the profile summary. Please try again later.
        </div>
      )}
      {/* TASK-656: fail-closed scope state — the server withholds unverified
          content; explain why and how to recover, never show a foreign fact. */}
      {data && data.scope_status === 'unverified' && (
        <div
          role="alert"
          data-testid="profile-scope-unverified"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900"
        >
          <p className="font-semibold">Source scope could not be verified</p>
          <p className="mt-0.5">
            The latest generated summary was withheld because its sources could not be
            verified as this client's own. Regenerate it from this client's meeting
            documents.
          </p>
        </div>
      )}
      {data && data.scope_status !== 'unverified' && belowConfidence && (
        <div className="text-[13px] text-muted-foreground">
          Summary confidence ({Math.round(data.confidence_score * 100)}%) is below the requested
          threshold.
        </div>
      )}
      {data && data.scope_status !== 'unverified' && !belowConfidence && (
        <div data-testid="client-profile-summary-content">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">
              {data.documents_analyzed} document{data.documents_analyzed === 1 ? '' : 's'} analysed
              {data.generated_at ? ` · ${new Date(data.generated_at).toLocaleDateString()}` : ''}
            </span>
            <span className="rounded-full bg-accent px-2 py-0.5 text-[12px] font-medium text-foreground">
              {Math.round(data.confidence_score * 100)}% confidence
            </span>
          </div>

          {visible.length > 0 ? (
            <dl className="space-y-3 text-[13px]">
              {visible.map((k) => (
                <div key={k}>
                  <dt className="font-semibold text-foreground">{SECTION_LABEL[k]}</dt>
                  <dd className="mt-0.5 whitespace-pre-line text-muted-foreground">
                    {sections![k]}
                  </dd>
                </div>
              ))}
            </dl>
          ) : active.sections?.length && sections && SECTION_KEYS.some((k) => (sections[k] ?? '').trim()) ? (
            <p className="text-[13px] text-muted-foreground">
              No matching sections for the current filter.
            </p>
          ) : data.profile_summary ? (
            <p className="whitespace-pre-line text-[13px] text-muted-foreground">
              {data.profile_summary}
            </p>
          ) : (
            <p className="text-[13px] text-muted-foreground">
              The stored summary is empty — regenerate it from a meeting's prepared documents.
            </p>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}
